import {
    catchError,
    debounceTime,
    exhaustMap,
    filter,
    map,
    mergeMap,
    share,
    switchMap,
    withLatestFrom,
} from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { ErrorsType } from '../../enum/errors.type';
import { FixedInvestActions } from './fixedInvest.action';
import { AppState } from '../../interfaces/app-state.interface';
import { FinanceService } from '../../services/finance.services';
import { DefaultResponse } from '../../models/form/DefaultResponse';
import { BackendService } from '../../services/backend/backend.service';
import { MyInvestmentsService } from '../../services/fixedincome/my-investments.service';
import { FixedIncomeApiHttpProvider } from '../../services/new-http/providers/fixed-income-api.http.service';
import { FgcOvertakenService } from '../../services/fixedincome/fgc-overtaken.service';

@Injectable()
export class FixedInvestEffects {

    /**
     * Obtem lista de produtos/cateiras.
     * @method getProducts
     */
    getProducts$ = createEffect(() => this.actions$
        .pipe(
            ofType(FixedInvestActions.getProducts),
            switchMap((action: any) => {
                return this.dataService
                    .post<DefaultResponse>('fixedincome/portfolio', action.payload)
                    .pipe(
                        map((result) => {
                            if (result.hasAlerta) {
                                return {
                                    type: FixedInvestActions.getProductsError,
                                    payload: {type: ErrorsType.Warning, value: result.messages},
                                };
                            }
                            if (result.hasError) {
                                return {
                                    type: FixedInvestActions.getProductsError,
                                    payload: {type: ErrorsType.Error, value: result.messages},
                                };
                            }
                            if (result.ok && result.hasValue) {
                                return {
                                    type: FixedInvestActions.getProductsSuccess,
                                    payload: result.value,
                                };
                            }
                            return {
                                type: FixedInvestActions.getProductsSuccess,
                                payload: result.value,
                            };
                        }),
                        catchError((error) => of({
                            type: FixedInvestActions.getProductsError,
                            payload: error,
                        })),
                    );
            }),
            share()
        )
    );

    /**
     * Obtem lista de produtos/cateiras.
     * @method getProductsIndexBydate
     */
    getProductsIndexBydate$ = createEffect(() => this.actions$
        .pipe(
            ofType(FixedInvestActions.getProductsIndexBydate),
            mergeMap((action: any) => {
                return this.dataService
                    .post<DefaultResponse>('fixedincome/portfolio', action.payload)
                    .pipe(
                        map((result) => {
                            if (result.hasAlerta) {
                                return {
                                    type: FixedInvestActions.getProductsIndexBydateError,
                                    payload: {type: ErrorsType.Warning, value: result.messages},
                                };
                            }
                            if (result.hasError) {
                                return {
                                    type: FixedInvestActions.getProductsIndexBydateError,
                                    payload: {type: ErrorsType.Error, value: result.messages},
                                };
                            }
                            if (result.ok && result.hasValue) {
                                return {
                                    type: FixedInvestActions.getProductsIndexBydateSuccess,
                                    payload: {
                                        value: result.value,
                                        time: action.payload.maturityPeriod,
                                    },
                                };
                            }
                            return {
                                type: FixedInvestActions.getProductsIndexBydateSuccess,
                                payload: {
                                    value: result.value,
                                    time: action.payload.maturityPeriod,
                                },
                            };
                        }),
                        catchError((error) => of({
                            type: FixedInvestActions.getProductsIndexBydateError,
                            payload: error,
                        })),
                    );
            }),
            share()
        )
    );

    /**
     * Obtem lista de produtos/cateiras.
     * @method getAdvices
     */
    getAdvices$ = createEffect(() => this.actions$
        .pipe(
            ofType(FixedInvestActions.getAdvices),
            exhaustMap((action: any) => {
                return this.dataService.post<DefaultResponse>('fixedincome/portfolio', {
                    valueToInvest: 10000,
                    maturityPeriod: 120,
                })
                    .pipe(
                        map((result) => {
                            if (result.hasAlerta) {
                                return {
                                    type: FixedInvestActions.getAdvicesError,
                                    payload: {type: ErrorsType.Warning, value: result.messages},
                                };
                            }
                            if (result.hasError) {
                                return {
                                    type: FixedInvestActions.getAdvicesError,
                                    payload: {type: ErrorsType.Error, value: result.messages},
                                };
                            }
                            if (result.ok && result.hasValue) {
                                return {
                                    type: FixedInvestActions.getAdvicesSuccess,
                                    payload: result.value,
                                };
                            }

                            return {
                                type: FixedInvestActions.getAdvicesSuccess,
                                payload: result.value,
                            };
                        }),
                        catchError((error) => of({
                                type: FixedInvestActions.getAdvicesError,
                                payload: error,
                            }),
                        ));
            }),
            share(),
    ));

    /**
     * Obtem um produto especifico
     * @method getProduct
     */
    getProduct$ = createEffect(() => this.actions$
        .pipe(
            ofType(FixedInvestActions.getProduct),
            switchMap((action: any) => {

                const {id, orderId, type, maturityPeriod, valueToInvest} = action.payload;

                return this.dataService
                    .get<DefaultResponse>(
                        // `fixedincome/bond?id=${action.payload.id}&fixedIncomeType=${action.payload.fixedIncomeType}`,
                        `fixedincome/bestbond/${id}/${type}?valueToInvest=${valueToInvest}&maturityPeriod=${maturityPeriod}&orderId=${orderId}`,
                    )
                    .pipe(
                        map((result) => {
                            if (result.hasAlerta) {
                                return {
                                    type: FixedInvestActions.getProductError,
                                    payload: {type: ErrorsType.Warning, value: result.messages},
                                };
                            }
                            if (result.hasError) {
                                return {
                                    type: FixedInvestActions.getProductError,
                                    payload: {type: ErrorsType.Error, value: result.messages},
                                };
                            }
                            if (result.ok && result.hasValue) {
                                return {
                                    type: FixedInvestActions.getProductSuccess,
                                    payload: result.value,
                                };
                            }

                            return {
                                type: FixedInvestActions.getProductSuccess,
                                payload: result,
                            };
                        }),
                        catchError((error) => of({
                            type: FixedInvestActions.getProductError,
                            payload: error,
                        })),
                    );
            }),
            share(),
        ));

    /**
     * Obtem um emissor especifico
     * @method getIssuer
     */
    getIssuer$ = createEffect(() => this.actions$
        .pipe(
            ofType(FixedInvestActions.getIssuer),
            withLatestFrom(this.store$.select((s) => s.fixedInvest)),
            mergeMap(([action, store]: any) => {
                if (store.issuer && store.issuer.id === action.payload.id) {
                    return of({
                        type: FixedInvestActions.getIssuerSuccess,
                        payload: store.issuer,
                    });
                } else {
                    return this.dataService.get<DefaultResponse>(`fixedincome/issuer/${action.payload.id}`)
                        .pipe(
                            map((result) => {
                                if (result.hasAlerta) {
                                    return {
                                        type: FixedInvestActions.getIssuerError,
                                        payload: {type: ErrorsType.Warning, value: result.messages},
                                    };
                                }
                                if (result.hasError) {
                                    return {
                                        type: FixedInvestActions.getIssuerError,
                                        payload: {type: ErrorsType.Error, value: result.messages},
                                    };
                                }
                                if (result.ok && result.hasValue) {
                                    return {
                                        type: FixedInvestActions.getIssuerSuccess,
                                        payload: result.value,
                                    };
                                }

                                return {
                                    type: FixedInvestActions.getIssuerSuccess,
                                    payload: result,
                                };
                            }),
                            catchError((error) => of({
                                type: FixedInvestActions.getIssuerError,
                                payload: error,
                            })),
                        );
                }
            }),
            share(),
    ));

    /**
     * Obtem os investimentos de uma simulação.
     * @method computeSimulation
     */
    computeSimulation = createEffect(() =>
        this.actions$.pipe(
            ofType(FixedInvestActions.computeSimulation),
            withLatestFrom(
                this.store$.select((s) => s.fixedInvest),
                this.store$.select((s) => s.research),
            ),
            switchMap(([action, fixedInvest, indexes]: any) => {
                const result = null;
                if (
                    fixedInvest.simulation &&
                    fixedInvest.simulation.length &&
                    fixedInvest.selectedProduct &&
                    fixedInvest.selectedProduct.bondList.length
                ) {

                    this.financeService.updateIndexes(indexes.index);

                    if (indexes.indexAgregated) this.financeService.updateIndexesAgregate(indexes.indexAgregated);
                    if (indexes.taxTable) this.financeService.updateTaxTable(indexes.taxTable);
                    if (indexes.indexQuote) this.financeService.updateIndexQuote(indexes.indexQuote);

                    const p = fixedInvest.selectedProduct.bondList.find((x) =>
                        x.id === fixedInvest.simulation[0].productId &&
                        x.type === fixedInvest.simulation[0].productType,
                    );

                    if (p) {
                        fixedInvest.simulation[0].results.dataInvestment = this.financeService.calculateGraphic(
                            fixedInvest.simulation[0].valueToInvest,
                            p.rateReturn,
                            p.indexer,
                            p.indexerId,
                            null,
                            10,
                            p.incomeTaxActive ? false : true,
                        );
                        fixedInvest.simulation[0].results.dataOtherBanks = this.financeService.calculateGraphic(
                            fixedInvest.simulation[0].valueToInvest,
                            '80',
                            'CDI',
                            7,
                            null,
                            10,
                        );
                        fixedInvest.simulation[0].results.dataSavings = this.financeService.calculateGraphic(
                            fixedInvest.simulation[0].valueToInvest,
                            '100',
                            'Poupança',
                            9,
                            null,
                            10,
                        );
                    }
                }
                return of({
                    type: FixedInvestActions.computeSimulationSuccess,
                    payload: fixedInvest.simulation,
                });
            }),
            share()
        )
    );

    /**
     * @deprecated - remover ate 01/01/2024
     * Obtem os investimentos de um usuário.
     * @method getMyInvestments
     */
    getMyInvestments$ = createEffect(() => this.actions$
        .pipe(
            ofType(FixedInvestActions.getMyInvestments),
            withLatestFrom(this.store$.select((s) => s.authentication)),
            filter(([action, auth]: any) => auth.currentUser && auth.currentUser.token),
            mergeMap(([action, auth]: any) => {
                return this.dataService
                    .get<DefaultResponse>(`fixedincome/bondsposition`)
                    .pipe(
                        map((result) => {
                            if (result.hasAlerta) {
                                return {
                                    type: FixedInvestActions.getMyInvestmentsError,
                                    payload: {type: ErrorsType.Warning, value: result.messages},
                                };
                            }
                            if (result.hasError) {
                                return {
                                    type: FixedInvestActions.getMyInvestmentsError,
                                    payload: {type: ErrorsType.Error, value: result.messages},
                                };
                            }
                            if (result.ok) {
                                return {
                                    type: FixedInvestActions.getMyInvestmentsSuccess,
                                    payload: this.myInvestmentsService.strategicalClassesMap(result.value),
                                };
                            }
                        }),
                        catchError((error) => of({
                            type: FixedInvestActions.getMyInvestmentsError,
                            payload: error,
                        })),
                    );
            }),
            share(),
    ));

    /**
     * Obtem as posições privadas de um usuário
     * @method getPositions
     */
    getPositions$ = createEffect(() => this.actions$
        .pipe(
            ofType(FixedInvestActions.getPositions),
            switchMap((action: any) => {
                return this.fixedIncomeApi.get('/v1/positions/list:private')
                    .pipe(
                        switchMap((privatePositions) => {
                            const issuerFgcOvertaken = this.fgcOvertakenService.extractFgcOvertaken(privatePositions);
                            return of(
                                {
                                    type: FixedInvestActions.getPositionsSuccess,
                                    payload: this.myInvestmentsService.strategicalClassesMap(privatePositions),
                                },
                                {
                                    type: FixedInvestActions.setIssuersFgcOvertaken,
                                    payload: issuerFgcOvertaken,
                                },
                                {
                                    type: FixedInvestActions.findAcceptedFgcOvertaken,
                                    payload: issuerFgcOvertaken,
                                }
                            );
                        }),
                        catchError((error) => of({type: FixedInvestActions.getPositionsError, payload: error})),
                    );
            }),
            share()
        )
    );

    /**
     * Obtem as posições segregadas (Tesouro Direto) de um usuário
     * @method getPositionsSegregated
     */
    getPositionsSegregated$ = createEffect(() => this.actions$
        .pipe(
            ofType(FixedInvestActions.getPositionsSegregated),
            switchMap((action: any) => {
                return this.fixedIncomeApi.get('/v1/positions/list:segregated')
                    .pipe(
                        map((treasuryPositions) => ({
                            type: FixedInvestActions.getPositionsSegregatedSuccess,
                            payload: treasuryPositions,
                        })),
                        catchError((error) => of({
                            type: FixedInvestActions.getPositionsSegregatedError,
                            payload: error,
                        })),
                    );
            }),
            share()
        )
    );

    buyProducts = createEffect(() =>
        this.actions$.pipe(
            ofType(FixedInvestActions.buyProduct),
            debounceTime(500),
            exhaustMap((action: any) => {
                return this.dataService.post<DefaultResponse>('fixedincome/acquirebond', action.payload.data, action.payload.token)
                    .pipe(
                        map((result) => {
                            if (result.ok) {
                                return {
                                    type: FixedInvestActions.buyProductSuccess,
                                    payload: result.value,
                                };
                            }

                            return {
                                type: FixedInvestActions.buyProductError,
                                payload: {type: ErrorsType.Error, value: result.messages},
                            };
                        }),
                        catchError((error) => of({
                            type: FixedInvestActions.buyProductError,
                            payload: error,
                        })),
                    );
            }),
            share()
        )
    );

    findAcceptedFgcOvertaken$ = createEffect(() => this.actions$
        .pipe(
            ofType(FixedInvestActions.findAcceptedFgcOvertaken),
            switchMap((action: any) => this.fgcOvertakenService.findAcceptedFgcOvertaken(action.payload)
                .pipe(
                    map((acceptedList) => {
                        return {
                            type: FixedInvestActions.findAcceptedFgcOvertakenSuccess,
                            payload: acceptedList,
                        };
                    }),
                    catchError((error) =>
                        of({
                            type: FixedInvestActions.findAcceptedFgcOvertakenError,
                            payload: error,
                        })),
                )
            ),
            share(),
        ));

    acceptFgcOvertaken$ = createEffect(() => this.actions$
        .pipe(
            ofType(FixedInvestActions.acceptFgcOvertaken),
            withLatestFrom(this.store$.select((s) => s.fixedInvest.fgcOvertaken && s.fixedInvest.fgcOvertaken.accepted)),
            switchMap(([action, acceptedList]: [any, any]) => this.fgcOvertakenService.acceptFgcOvertaken(action.payload.issuers, acceptedList)),
            map((data) => {
                return {
                    type: FixedInvestActions.findAcceptedFgcOvertakenSuccess,
                    payload: data,
                };
            }),
            share(),
    ));

    constructor(
        private actions$: Actions,
        private dataService: BackendService,
        private financeService: FinanceService,
        private myInvestmentsService: MyInvestmentsService,
        private fixedIncomeApi: FixedIncomeApiHttpProvider,
        private fgcOvertakenService: FgcOvertakenService,
        private store$: Store<AppState>,
    ) {
    }

}
